/**
 * CGM-OPERATIONAL-CONTROL-R3 — parcel command confirmation dialog.
 *
 * The first click never commands. This dialog restates the exact parcel, action
 * and physical channel (from the canonical binding) before the single POST.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PendingParcelConfirmation } from '../hooks/useParcelValveCommand';

interface ParcelConfirmDialogProps {
  pending: PendingParcelConfirmation;
  displayName?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const ParcelConfirmDialog: React.FC<ParcelConfirmDialogProps> = ({
  pending,
  displayName,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation('agrisafeR1');
  const parcelName = displayName ?? pending.binding.parcel;
  const opening = pending.action === 'open';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="parcel-confirm-dialog"
    >
      <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] p-5 shadow-lg">
        <h2 className="text-lg font-semibold text-[var(--text-primary)]" data-testid="parcel-confirm-title">
          {opening
            ? t('parcel.confirm.openTitle', { parcel: parcelName })
            : t('parcel.confirm.closeTitle', { parcel: parcelName })}
        </h2>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--text-secondary)]">{t('parcel.confirm.parcel')}</dt>
            <dd className="font-medium text-[var(--text-primary)]" data-testid="parcel-confirm-parcel">
              {parcelName}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--text-secondary)]">{t('parcel.confirm.channel')}</dt>
            <dd className="font-medium text-[var(--text-primary)]" data-testid="parcel-confirm-channel">
              {pending.binding.controller_node_id} · {pending.binding.channel_id}
            </dd>
          </div>
        </dl>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="parcel-confirm-cancel"
            className="min-h-[44px] rounded-xl border border-[var(--border-medium)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--gray-50)]"
          >
            {t('parcel.confirm.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="parcel-confirm-proceed"
            className="min-h-[44px] rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            {opening ? t('parcel.confirm.proceedOpen') : t('parcel.confirm.proceedClose')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParcelConfirmDialog;
